import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'

import { PermissionSuffix } from '../../_generated/api/user-management'
import t from '../../tools/t'
import ArchivedStateLabel from '../common/pure/ArchivedStateLabel'
import { useUserContext } from '../contexts/UserContext'
import { useToastContext } from '../contexts/ToastContext'
import { ContentColumn, LayoutColumnContainer, LayoutRowContainer } from '../common/pure/layout'
import Heading from '../common/pure/Heading'
import { DropdownField, TextField } from '../common/pure/input-fields'
import { Column, Row } from '../common/pure/FlexContainer'
import { pi } from '../common/pure/PrimeIcon'
import { ErrorMessageScreen } from '../common/pure/MessageScreen'
import { PermissionSwitchList } from '../common/pure/SwitchList'
import FullScreenLoading from '../common/FullScreenLoading'

export default function UserProfileScreen() {
    const Toast = useToastContext()
    const {
        me,
        selectedOrganization,
        usersLoading,
        usersError,
        can,
        updateUser,
        resetPassword,
        restoreUser,
        getUserProfileById,
    } = useUserContext()
    const { userProfileId } = useParams()
    const [selectedUserProfile, setSelectedUserProfile] = useState(
        getUserProfileById(userProfileId)
    )
    const isArchived = !!selectedUserProfile?.isArchived

    // Editable fields
    const [name, setName] = useState(selectedUserProfile?.name ?? '')
    const [email, setEmail] = useState(selectedUserProfile?.email ?? '')
    const [association, setAssociation] = useState<string>(selectedUserProfile?.association ?? '')
    const [selectedPermissions, setSelectedPermissions] = useState(
        selectedUserProfile?.permissions ?? []
    )
    const canWriteUsers = can(PermissionSuffix.UserWrite)

    useEffect(() => {
        if (!selectedUserProfile) return
        setSelectedPermissions(selectedUserProfile.permissions)
        setName(selectedUserProfile.name)
        setEmail(selectedUserProfile.email)
        setAssociation(selectedUserProfile.association)
    }, [selectedUserProfile])

    useEffect(() => {
        setSelectedUserProfile(getUserProfileById(userProfileId))
    }, [userProfileId, getUserProfileById])

    const submitUserUpdate = async () => {
        if (!selectedUserProfile || isArchived) return
        const updatedName = name || selectedUserProfile.name
        const updatedEmail = email || selectedUserProfile.email
        const user = await updateUser(selectedUserProfile.id, {
            name: updatedName,
            email: updatedEmail,
            vatNo: association,
            permissions: selectedPermissions,
        })
        if (!user) return
        setSelectedUserProfile(user)
        Toast.completed('update', 'user')
    }

    const onRestoreUser = async () => {
        if (!selectedUserProfile) return
        await restoreUser(selectedUserProfile.id)
    }

    const onResetPasswordRequest = async () => {
        if (!selectedUserProfile || isArchived) return
        const success = await resetPassword(selectedUserProfile.id)
        const recipient = me?.id === selectedUserProfile.id ? 'You ' : selectedUserProfile.name
        if (success)
            Toast.success(
                'Password reset initiated',
                recipient + ' will receive a password-reset email.'
            )
        else Toast.danger('Password reset failed', 'Please contact your administrator.')
    }

    const associationSource = selectedOrganization?.associations?.map((r) => ({
        label: `${r.vatNo}\t${r.name}`,
        value: r.vatNo,
    }))

    if (!selectedUserProfile) {
        if (usersLoading) return <FullScreenLoading />
        return <ErrorMessageScreen title={usersError?.name} message={usersError?.message} />
    }

    return (
        <LayoutColumnContainer>
            <Row arrangement="spaced" alignment="center">
                {isArchived && (
                    <ArchivedStateLabel onRestore={canWriteUsers ? onRestoreUser : undefined} />
                )}
                <Heading size={2}>{selectedUserProfile?.name}</Heading>
                {!isArchived && canWriteUsers && (
                    <Button
                        icon={pi('save')}
                        label={t('common_save_all')}
                        onClick={submitUserUpdate}
                        disabled={isArchived}
                    />
                )}
            </Row>
            <LayoutRowContainer>
                <ContentColumn columns={1}>
                    <Card title={t('common_details')}>
                        <Column gap={0.5}>
                            <TextField
                                placeholder={t('user_field_full_name')}
                                value={name}
                                disabled={!canWriteUsers || isArchived}
                                onChange={(e) => setName(e.target.value)}
                            />
                            <TextField
                                placeholder={t('user_field_email')}
                                value={email}
                                disabled={!canWriteUsers || isArchived}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                            <DropdownField
                                placeholder={t('user_field_association')}
                                value={association}
                                options={associationSource}
                                disabled={!canWriteUsers || isArchived}
                                onChange={(data) => setAssociation(data.value)}
                            />
                            <Button
                                text
                                severity="secondary"
                                label={t('reset_password')}
                                onClick={onResetPasswordRequest}
                                disabled={!canWriteUsers || isArchived}
                            />
                        </Column>
                    </Card>
                </ContentColumn>
                <ContentColumn columns={2}>
                    <Card title={t('common_permissions')}>
                        <PermissionSwitchList
                            onChange={setSelectedPermissions}
                            selectedPermissions={selectedPermissions}
                            disabled={!canWriteUsers || isArchived}
                        />
                    </Card>
                </ContentColumn>
            </LayoutRowContainer>
        </LayoutColumnContainer>
    )
}
